/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { ChangeEvent, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Pagination,
  Table,
  TableContent,
  TableHeader,
  TableHeaderData,
  DensityMostIcon,
  Input,
  SearchIcon,
  CloseIcon,
  Checkbox,
  isNotEmpty,
  ModelCreationIcon,
  Banner,
  Tooltip,
  FilterIcon
} from "@forge/common";
import { useTranslation } from "react-i18next";

import { SortDirProps } from "../../models/devicelist";
import { useNavigate } from "react-router";
import { LOCALE_CONST, ROUTE_PATH } from "../../utils/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
// Redux 
import { useSelector } from "react-redux";
//@Store
import { RootState } from "../../store";

interface CustomizedTableProps {
  title: string;
  tableHeaderData: TableHeaderData[];
  tableContentData: any;
  initialPage: number;
  totalItems: number;
  totalPages: number;
  maxPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  showHideIconClick?: () => void;
  filterIconClick?: () => void;
  assignSitesClick?: () => void;
  searchOnChange?: (value: string) => void;
  searchValue?: string;
  tableData?: any;
  onSearchClear?: () => void;
  isSearch?: boolean;
  onUpdateSort?: (dir: SortDirProps, id: string) => void;
  canResize?: boolean;
}

const CustomizedTable: React.FC<CustomizedTableProps> = ({
  title,
  tableHeaderData,
  tableContentData,
  initialPage,
  totalItems,
  totalPages,
  maxPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  showHideIconClick,
  filterIconClick,
  searchOnChange,
  searchValue,
  tableData,
  onSearchClear,
  isSearch,
  onUpdateSort,
  canResize
}) => {
  const { deviceListFilter } = useSelector((state: RootState) => state.global);
  const flags = useFlags();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedItems, setSelectedItems] = useState<Record<string, any | undefined>>({});
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const [isOrgNameSame, setIsOrgNameSame] = useState<boolean>(false);

  useEffect(() => {
    setSelectedItems(
      tableData.reduce((acc: Record<string, any | undefined>, cur: any) => ({
        ...acc,
        [cur.id]: undefined
      }), {})
    );
  }, [tableData]);

  const toggleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const selected = tableData.reduce((acc: Record<string, any | undefined>, cur: any) => ({
      ...acc,
      [cur.id]: (isChecked && cur.status !== "REGISTRATION_PENDING" && cur.status !== "DEACTIVATED" && cur.organizationId !== null) ? cur : undefined
    }), {});
    setSelectedItems(selected);

    const selecData = isChecked ? tableData.filter((item: any) => item.status !== "REGISTRATION_PENDING" && item.status !== "DEACTIVATED" && item.organizationId !== null) : [];
    setSelectedData(selecData);
    checkOrganizationNames(selecData);
  };

  const onItemSelect = (data: any, isChecked: boolean) => {
    setSelectedItems(prevState => {
      const newState = {
        ...prevState,
        [data.id]: isChecked ? data : undefined
      };
      return newState;
    });

    const selectedDataID = tableData.find((item: any) => item.id === data.id);

    if (isChecked) {
      setSelectedData(prevState => {
        const newState = [...prevState, selectedDataID];
        checkOrganizationNames(newState);
        return newState;
      });
    } else {
      setSelectedData(prevState => {
        const newState = prevState.filter(item => item.id !== data.id);
        checkOrganizationNames(newState);
        return newState;
      });
    }
  };

  const checkOrganizationNames = (dataSelec: any[]) => {
    if (dataSelec.length === 0) return;
    const firstOrgName = dataSelec[0].organizationName;
    const allSame = dataSelec.every(item => item.organizationName === firstOrgName);
    setIsOrgNameSame(allSame);
  };

  const selectedItemsIds = Object.keys(selectedItems)
  .filter(key => !selectedItems[key]?.disabled)
  .map(key => selectedItems[key]?.id)
  .filter(Boolean);  

  const tableDataNoPendingDeactivated = tableData.filter((item: any) => item.status !== "REGISTRATION_PENDING" && item.status !== "DEACTIVATED" && item.organizationId !== null);
  const renderHeaderProps = () => {
    const allDisabled = tableDataNoPendingDeactivated.length === 0;
    const headerProps: any = {
      data: flags.showBulkAssignment?.length > 0 ? ([{
        id: 'select-all-cell',
        content: (
          (!allDisabled)  ?
          <Checkbox
            id="select-all"
            onChange={toggleSelectAll}
            indeterminate={isNotEmpty(selectedItemsIds) && selectedItemsIds.length !== tableDataNoPendingDeactivated.length}
            checked={selectedItemsIds.length === tableDataNoPendingDeactivated.length}
            data-testid="select-all-test"
          /> : <Checkbox id="select-all" disabled />
        ),
        className: 'w-12' // Set fixed width for the checkbox column
      }, ...tableHeaderData]) : (
        tableHeaderData
      )
    };

    if (onUpdateSort) {
      headerProps.onSort = (dir: SortDirProps, id: string) => onUpdateSort(dir, id);
    }

    if(canResize) {
      headerProps.canResize = true;
    }

    return headerProps;
  };

  const assignSitesLinkClick = () => {
    const selectedOrg = selectedData[0]?.organizationName;
    const selectedIds = selectedData.map(item => item.deviceId).join(',');
    const selectOrgId = selectedData[0]?.organizationId;
    const url = `${ROUTE_PATH.bulkAssignment}?org=${selectedOrg}&ids=${selectedIds}&orgId=${selectOrgId}`;
    navigate(url);
  };

  const getPaginationInfoLabel = (pageNumber: number, pgSize: number, totItems: number) => {
    // Calculate the starting index for the current page
    const startItem = (pageNumber - 1) * pgSize + 1;

    // Calculate the ending index for the current page, ensuring it does not exceed totalItems
    const endItem = Math.min(startItem + pgSize - 1, totItems);

    return t('numberOfItems', { pageSize: `${startItem} - ${endItem}`, totalRecords: totItems ?? 0 });
  };


  return (
    <>
      {(isNotEmpty(selectedItemsIds) && !isOrgNameSame && flags.showBulkAssignment?.length > 0) &&
        <Banner 
          variant="inline" 
          type="warning" 
          title={t(LOCALE_CONST.gwBelongsDifferentOrgWarn)} 
          description={t(LOCALE_CONST.actionNotAllowed)}
          canClose={true}
        />
      }
      <Card className="mt-3 customized-table-card">        
        <CardHeader
          title={title}
          subtitle={(isNotEmpty(selectedItemsIds) && flags.showBulkAssignment?.length > 0) ? `${selectedItemsIds.length} item selected` : ''}
          className="card-header-title"
          slotRight={
            <div className="flex items-center gap-3 table-header-right-action">
              {isNotEmpty(selectedItemsIds) && (
                <>
                  {!isOrgNameSame ? (
                    <Tooltip content={t(LOCALE_CONST.notAllowedSelecDevices)} position="bottom">
                      <Button
                        disabled
                        icon={<ModelCreationIcon />}
                        variant="quiet-primary"        
                        className={`${flags.showBulkAssignment?.length === 0 ? 'hidden' : ''}`}                
                      >
                        {t(LOCALE_CONST.assignSites)}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Button
                      icon={<ModelCreationIcon />}
                      variant="quiet-primary"
                      onClick={assignSitesLinkClick}
                      data-testid="assign-sites-test"
                      className={`${flags.showBulkAssignment?.length === 0 ? 'hidden' : ''}`}
                    >
                      {t(LOCALE_CONST.assignSites)}
                    </Button>
                  )}
                </>
              )}
              <Input
                type="text"
                data-testid="search-test"
                placeholder={t(LOCALE_CONST.search)}
                id="search"
                onChange={(e) => {
                  searchOnChange && searchOnChange(e.target.value);
                }}
                value={searchValue}
                prefix={<SearchIcon strokeWidth={2} />}
                suffix={
                  searchValue ? (
                    <div
                      className="cursor-pointer"
                      data-testid="search-close-test"
                      onClick={onSearchClear}
                    >
                      <CloseIcon data-testid="search-close" />
                    </div>
                  ) : (
                    <></>
                  )
                }
                size="regular"
              />
              {filterIconClick && (
                <Button className = {`${deviceListFilter.isFilterApplied ? 'flitre-icon-dot' : ''}`}
                  aria-label="Settings"
                  icon={<FilterIcon strokeWidth={2.5} />}
                  variant="tertiary"
                  onClick={filterIconClick}
                  iconPosition="left"
                  size="regular"
                  data-testid="filter-icon-test"
                />
              )}
              {showHideIconClick && (
                <Button
                  aria-label="show-hide"
                  icon={<DensityMostIcon strokeWidth={2} />}
                  variant="tertiary"
                  className="show-hide-icon"
                  onClick={showHideIconClick}
                  data-testid="column-icon-test"
                />
              )}
            </div>
          }
        ></CardHeader>
        {tableData && tableData.length > 0 ? (
          <>
            <CardContent x="none" top="none" className="custom_table_design">
              <Table onCard fixedLastColumn>
                <TableHeader {...renderHeaderProps()} />
                <TableContent data={tableContentData.map((row: any, i: number) => {
                  const _isSelected = selectedItems[row.id] !== undefined;
                  const _isPending = tableData.some((item: any) => item.id === row.id && item.status === 'REGISTRATION_PENDING');
                  const _isDeactivated = tableData.some((item: any) => item.id === row.id && item.status === 'DEACTIVATED');
                  const _isOrgNull = tableData.some((item: any) => item.id === row.id && item.organizationId === null);
                  return {
                    ...row,
                    cell: flags.showBulkAssignment?.length > 0 ? ([
                      {
                        content: (
                          <>
                            {(!_isPending && !_isDeactivated && !_isOrgNull && flags.showBulkAssignment?.length > 0) ? (
                              <Checkbox
                                id={`table-row-${i}-select`}
                                checked={_isSelected && !_isPending && !_isDeactivated && !_isOrgNull}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => onItemSelect(row, e.target.checked)}
                                data-testid={`table-row-${i}-select-test`}
                              />
                            ) : (
                              <Checkbox
                                id={`table-row-${i}-select`}
                                disabled
                              />
                            )}
                          </>
                        ),
                        className: 'w-[50px]' // Set fixed width for the checkbox column
                      },
                      ...row.cell
                    ]) : row.cell
                  };
                })} />
              </Table>
            </CardContent>
            <CardContent className="border-t border-gray-200 dark:border-gray-700">
              <Pagination
                currentPage={initialPage}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                maxPages={maxPages}
                pageSize={pageSize}
                pageSizeOptions={[10, 15, 20, 50, 100]}
                totalItems={totalItems}
                totalPages={totalPages}
                showPageGoTo
                showPageInfo
                showPageSizeList
                pageGoToLabel={t(LOCALE_CONST.goToPage)}
                pageSizeListLabel={t(LOCALE_CONST.rowsPerPage)}
                pageInfoLabel={getPaginationInfoLabel(initialPage, pageSize, (totalItems ?? 0))}
              />
            </CardContent>
          </>
        ) : (
          <CardContent className="h-96 place-items-center grid">
            <p>
              {isSearch
                ? t(LOCALE_CONST.noResultsFound)
                : t(LOCALE_CONST.noResultFoundText)}
            </p>
          </CardContent>
        )}
      </Card>
    </>
  );
};

export default CustomizedTable;
